// Device catalog helpers: filtering, grouping, and custom-device management.

const MIN_DIMENSION = 100;
const MAX_DIMENSION = 4000;

// The year filter lists this many most-recent release years explicitly;
// "Older" then covers only devices with a known year before the oldest one.
export const MAX_LISTED_YEARS = 8;

export const SIZE_BUCKETS = {
  compact: { label: "Compact (<380px)", test: (w) => w < 380 },
  standard: { label: "Standard (380–409px)", test: (w) => w >= 380 && w < 410 },
  large: { label: "Large (410–459px)", test: (w) => w >= 410 && w < 460 },
  xlarge: { label: "Extra large (460px+)", test: (w) => w >= 460 },
};

export function getSizeBucket(device) {
  const w = Math.min(device.width, device.height);
  return Object.keys(SIZE_BUCKETS).find((key) => SIZE_BUCKETS[key].test(w)) || "xlarge";
}

export function filterDevices(
  devices,
  { search = "", category = "all", brand = "all", year = "all", size = "all", orientation = "all" } = {}
) {
  const term = search.trim().toLowerCase();
  const olderThan = year === "other" ? getYearFilterOptions(devices).olderThan : null;

  return devices.filter((device) => {
    const matchesSearch =
      !term ||
      device.name.toLowerCase().includes(term) ||
      device.brand.toLowerCase().includes(term) ||
      (device.keywords || []).some((keyword) => keyword.toLowerCase().includes(term));

    const matchesCategory = category === "all" || device.category === category;
    const matchesBrand = brand === "all" || device.brand === brand;
    const matchesYear =
      year === "all" ||
      (year === "other"
        ? olderThan !== null && typeof device.releaseYear === "number" && device.releaseYear < olderThan
        : String(device.releaseYear) === year);
    const matchesSize = size === "all" || getSizeBucket(device) === size;
    const matchesOrientation = orientation === "all" || device.defaultOrientation === orientation;

    return matchesSearch && matchesCategory && matchesBrand && matchesYear && matchesSize && matchesOrientation;
  });
}

export function groupByCategory(devices) {
  return devices.reduce((groups, device) => {
    const list = groups[device.category] || [];
    list.push(device);
    groups[device.category] = list;
    return groups;
  }, {});
}

export function groupByBrand(devices) {
  return devices.reduce((groups, device) => {
    const list = groups[device.brand] || [];
    list.push(device);
    groups[device.brand] = list;
    return groups;
  }, {});
}

export function getBrands(devices) {
  return Array.from(new Set(devices.map((d) => d.brand))).sort();
}

export function getReleaseYears(devices) {
  return Array.from(new Set(devices.map((d) => d.releaseYear).filter(Boolean))).sort(
    (a, b) => b - a
  );
}

/**
 * Year options for the release-year filter: the most recent years listed
 * explicitly, plus whether an "Older" bucket (known year < `olderThan`) would
 * match anything. Devices without a release year are in neither.
 */
export function getYearFilterOptions(devices) {
  const years = getReleaseYears(devices).slice(0, MAX_LISTED_YEARS);
  const olderThan = years.length > 0 ? years[years.length - 1] : null;
  const hasOlder =
    olderThan !== null &&
    devices.some((d) => typeof d.releaseYear === "number" && d.releaseYear < olderThan);
  return { years, olderThan, hasOlder };
}

export function validateDeviceDimensions(width, height) {
  const w = Number(width);
  const h = Number(height);

  if (!Number.isFinite(w) || !Number.isFinite(h)) {
    return { valid: false, error: "Width and height must be numbers." };
  }
  if (w < MIN_DIMENSION || h < MIN_DIMENSION) {
    return { valid: false, error: `Minimum size is ${MIN_DIMENSION}×${MIN_DIMENSION}px.` };
  }
  if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
    return { valid: false, error: `Maximum size is ${MAX_DIMENSION}×${MAX_DIMENSION}px.` };
  }
  return { valid: true, error: null };
}

function slugify(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/["']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-+|-+$)/g, "");
}

export function buildCustomDeviceId(name, existingDevices) {
  const base = slugify(name) || "custom-device";
  const existingIds = new Set(existingDevices.map((d) => d.id));
  let id = `custom-${base}`;
  let suffix = 2;
  while (existingIds.has(id)) {
    id = `custom-${base}-${suffix}`;
    suffix += 1;
  }
  return id;
}

export function isDuplicateDeviceName(name, existingDevices, ignoreId = null) {
  const normalized = name.trim().toLowerCase();
  return existingDevices.some(
    (d) => d.id !== ignoreId && d.name.trim().toLowerCase() === normalized
  );
}

/**
 * Creates a new custom device object. Caller is responsible for persisting it.
 * Throws if validation fails, so callers should validate before persisting
 * or catch synchronously when calling from a form submit handler.
 */
export function createCustomDevice({ name, width, height }, existingDevices) {
  const trimmedName = (name || "").trim();
  if (!trimmedName) {
    throw new Error("Device name is required.");
  }
  if (isDuplicateDeviceName(trimmedName, existingDevices)) {
    throw new Error("A device with this name already exists.");
  }

  const dimensionCheck = validateDeviceDimensions(width, height);
  if (!dimensionCheck.valid) {
    throw new Error(dimensionCheck.error);
  }

  return {
    id: buildCustomDeviceId(trimmedName, existingDevices),
    name: trimmedName,
    brand: "Custom",
    category: "custom",
    width: Number(width),
    height: Number(height),
    pixelRatio: 1,
    userAgent: "",
    supportsRotation: true,
    featured: false,
    isCustom: true,
    releaseYear: null,
    defaultOrientation: Number(width) > Number(height) ? "landscape" : "portrait",
    keywords: [trimmedName.toLowerCase()],
  };
}
